/**
 * Attached-mode source-group linkage (RFC-120 §3).
 *
 * The converter stamps a deterministic `sourceGroupId` (SHA-1 prefix of the
 * source E57 path) on BOTH artifacts it derives from one scan file:
 *   • the panorama tour scene item → `extensions.panoramaBubble.sourceGroupId`
 *   • the point-cloud scene item   → `extensions.pointcloud.sourceGroupId`
 *
 * `normalizeSceneItem` promotes those blocks onto `layer.style.panoramaBubble`
 * / `layer.style.pointcloud` (with the raw copy under
 * `layer.style.scenePackageExtensions`). This module reads them back and
 * pairs catalog layers so the shell can offer a bubble-tour ↔ point-cloud
 * representation switch when both exist in one scene.
 */

import type { Layer } from "@geosoft/viewer-sdk";
import { isPanoramaBubbleLayer } from "./sceneTreePanoramaBubble";

/** A panorama tour and a point cloud converted from the same source scan. */
export interface PanoramaPointCloudLink {
  readonly sourceGroupId: string;
  readonly panoramaLayerId: string;
  readonly pointCloudLayerId: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readSourceGroupId(
  layer: Layer,
  extensionKey: "panoramaBubble" | "pointcloud",
): string | null {
  const style = layer.style;
  if (!isRecord(style)) return null;
  const candidates: unknown[] = [style[extensionKey]];
  const rawExtensions = style.scenePackageExtensions;
  if (isRecord(rawExtensions)) candidates.push(rawExtensions[extensionKey]);
  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    const id = candidate.sourceGroupId;
    if (typeof id === "string" && id.length > 0) return id;
  }
  return null;
}

/** `extensions.panoramaBubble.sourceGroupId` for a panorama-bubble layer. */
export function readPanoramaBubbleSourceGroupId(layer: Layer): string | null {
  if (!isPanoramaBubbleLayer(layer)) return null;
  return readSourceGroupId(layer, "panoramaBubble");
}

/** `extensions.pointcloud.sourceGroupId` for a point-cloud layer. */
export function readPointCloudSourceGroupId(layer: Layer): string | null {
  if (layer.kind !== "pointcloud") return null;
  return readSourceGroupId(layer, "pointcloud");
}

/**
 * Pair every panorama-bubble layer with the point-cloud layer sharing its
 * `sourceGroupId`. Layers without the extension never match (legacy
 * standalone tours keep today's behaviour). When several point clouds carry
 * the same group id (e.g. COPC + Potree alternates of one E57), the first
 * one in catalog order wins — alternates point at the same source data.
 */
export function findPanoramaPointCloudLinks(
  catalog: readonly Layer[],
): PanoramaPointCloudLink[] {
  const pointCloudByGroup = new Map<string, string>();
  for (const layer of catalog) {
    const groupId = readPointCloudSourceGroupId(layer);
    if (groupId && !pointCloudByGroup.has(groupId)) {
      pointCloudByGroup.set(groupId, layer.id);
    }
  }
  if (pointCloudByGroup.size === 0) return [];

  const links: PanoramaPointCloudLink[] = [];
  for (const layer of catalog) {
    const groupId = readPanoramaBubbleSourceGroupId(layer);
    if (!groupId) continue;
    const pointCloudLayerId = pointCloudByGroup.get(groupId);
    if (!pointCloudLayerId) continue;
    links.push({
      sourceGroupId: groupId,
      panoramaLayerId: layer.id,
      pointCloudLayerId,
    });
  }
  return links;
}
